/**
 * 智能剪辑底部参数面板（样式对齐视频节点底部参数面板 EditorPanel：
 * 玻璃容器 #262626/95 backdrop-blur-2xl + 行内下拉触发器 + #262626 菜单（激活项 bg-white/5）+ 32×32 圆角8px 白底发送键 send-arrow.svg）
 * 结构：顶行「+ 参考」与全屏按钮；中部多行效果描述输入；底行「模式」「规格」下拉 + 发送键。
 * §4.1.3「发送 = 携带上下文打开面板」：发送携带 prompt / mode / spec 打开全屏面板。
 * 草稿 state 由 GenerateNode 提升（镜像脚本节点，取消选中不丢输入）。
 * v0.34 按钮行为对齐脚本节点底部面板（ScriptQuickInput）：
 * - 左上「+ 参考」= P2 接线点占位（禁用态，不再打开剪辑面板）；已连接上游素材时同位展示素材缩略图（静态展示不可点）。
 * - 右上 Maximize2 = 全屏按钮：createPortal 到 body 的 fixed 居中展开面板（遮罩压暗 + 标题 + 已连接素材 + 大编辑区
 *   + 同款底栏 + 右下角拖拽手柄，Minimize2 / 点遮罩还原），不再打开全屏剪辑面板。
 * - 底行右组补齐翻译与积分显示（参考脚本节点底部面板 / EditorPanel）：Languages 中英互译（共享设施
 *   useNodeTranslate，对话模型三级回退显式携带，翻译中转 Loader2 并禁发）+ ⚡积分预估（积分系统 P1 实装，
 *   发送仅打开智能剪辑面板不扣费，生成在后续面板按所选模型扣费，此处按翻译对话模型口径预估）。
 */
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Languages, Loader2, Maximize2, Minimize2, Play, Plus } from 'lucide-react';
import { AppNode } from '@/types';
import { useAgentStore } from '@/stores/agentStore';
import { getModelCost } from '@/utils/credits';
import {
  CanvasRatio,
  CanvasResolution,
  EditMode,
  EDIT_MODES,
  SmartEditSession,
} from '@/types/smartEdit';
// 全屏展开面板尺寸边界（min 520×360 / max 视口 92%）+ 翻译共享设施（脚本面板 / EditorPanel 同源）
import { clampExpandPanelSize, useNodeTranslate, isChatModel, pickTranslateModel } from '../../hooks';

interface SmartEditQuickInputProps {
  data: AppNode;
  /** 连接素材态：首个上游视频（「+参考」位置显示已连接素材缩略图，规格文案附真实时长） */
  upstreamVideo?: { videoUri: string; durationSec?: number } | null;
  draftText: string;
  onDraftChange: (text: string) => void;
  /** 发送：携带 prompt / mode / spec 打开面板（§4.1.3 发送 = 携带上下文打开面板） */
  onSend: (session: SmartEditSession) => void;
}

const RATIOS: CanvasRatio[] = ['16:9', '9:16', '1:1'];
const RESOLUTIONS: CanvasResolution[] = ['720P', '1080P'];

const SmartEditQuickInputComponent: React.FC<SmartEditQuickInputProps> = memo(({
  data,
  upstreamVideo,
  draftText,
  onDraftChange,
  onSend,
}) => {
  const [mode, setMode] = useState<EditMode>('default');
  const [ratio, setRatio] = useState<CanvasRatio>('16:9');
  const [resolution, setResolution] = useState<CanvasResolution>('720P');
  const [openMenu, setOpenMenu] = useState<'mode' | 'spec' | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // 全屏展开态与面板尺寸（右下角手柄拖拽调整，镜像脚本节点 v1.4.17）；组件随取消选中卸载，expanded 自动复位为内嵌模式
  const [expanded, setExpanded] = useState(false);
  const [panelSize, setPanelSize] = useState(() => clampExpandPanelSize(800, 520));
  const panelSizeRef = useRef(panelSize);
  panelSizeRef.current = panelSize;

  // 展开态下窗口缩小时兜底 clamp，保证面板永不超出视口
  useEffect(() => {
    if (!expanded) return;
    const onResize = () => setPanelSize((s) => clampExpandPanelSize(s.w, s.h));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [expanded]);

  // 右下角手柄：mousedown 记起点 → window mousemove 按增量 clamp 更新尺寸 → mouseup 收尾
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const { w: startW, h: startH } = panelSizeRef.current;
    const onMove = (ev: MouseEvent) => {
      setPanelSize(clampExpandPanelSize(startW + (ev.clientX - startX), startH + (ev.clientY - startY)));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  // 点击面板外关闭下拉菜单（capture 阶段；菜单项点击后自行收起，不受影响）
  useEffect(() => {
    if (!openMenu) return;
    const close = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpenMenu(null);
    };
    document.addEventListener('mousedown', close, true);
    return () => document.removeEventListener('mousedown', close, true);
  }, [openMenu]);

  // 翻译（中英互译·自动方向）：复用共享设施 useNodeTranslate（脚本面板 / EditorPanel 同款），译文替换输入草稿；
  // 对话模型三级回退挑选（翻译走 LLM 分支，路由层对空 modelId 有 400 拦截，需显式携带）；
  // 执行状态按节点 id 存模块级 store，跨面板卸载周期存活；翻译期间禁用发送（防原文被误发）
  const agentModels = useAgentStore((s) => s.availableModels);
  const ensureAvailableModels = useAgentStore((s) => s.ensureAvailableModels);
  useEffect(() => { ensureAvailableModels(); }, [ensureAvailableModels]);
  const chatModels = useMemo(() => agentModels.filter(isChatModel), [agentModels]);
  const translateModelId = useMemo(() => pickTranslateModel(chatModels)?.id || '', [chatModels]);
  const { translating, translate: handleTranslate } = useNodeTranslate({
    nodeId: data.id,
    text: draftText,
    modelId: translateModelId,
    onResult: onDraftChange,
  });

  const handleSend = () => {
    if (translating) return;
    setOpenMenu(null);
    onSend({
      prompt: draftText.trim() || undefined,
      mode: mode === 'default' ? undefined : mode,
      spec: { ratio, resolution },
    });
    onDraftChange('');
  };

  const modeName = EDIT_MODES.find((m) => m.id === mode)?.name ?? '默认模式';

  // 已连接素材：真实时长展示（缩略图徽标 + 规格文案「16:9 · 720P · 30s」同款）
  const durationLabel = upstreamVideo?.durationSec && upstreamVideo.durationSec > 0
    ? `${Math.round(upstreamVideo.durationSec)}s`
    : undefined;

  // 对齐 EditorPanel 行内下拉触发器（ModelConfigEditor 同款）：px-3 py-1.5 / rounded-lg / hover:bg-white/5，展开时 bg-white/5 高亮
  const triggerCls = (open: boolean) =>
    `flex items-center gap-1.5 px-3 py-1.5 rounded-lg cursor-pointer transition-colors text-xs font-medium text-white ${
      open ? 'bg-white/5' : 'hover:bg-white/5'
    }`;
  // 对齐 EditorPanel 菜单项（#262626 菜单内）：激活 bg-white/5 text-white，未激活 text-white/60 hover:bg-white/10
  const menuItemCls = (active: boolean) =>
    `w-full flex items-center justify-between px-3 py-2 text-xs font-medium cursor-pointer transition-colors ${
      active ? 'text-white bg-white/5 hover:bg-white/10' : 'text-white/60 hover:bg-white/10'
    }`;
  // 对齐 EditorPanel 参数按钮（高清放大「放大倍数」同款）：激活 bg-white/20 text-white border-white/40
  const chipCls = (active: boolean) =>
    `h-7 px-2.5 rounded-xl text-[12px] font-medium transition-all border cursor-pointer ${
      active
        ? 'bg-white/20 text-white border-white/40 shadow-[0_0_8px_rgba(255,255,255,0.1)]'
        : 'text-white/50 border-white/10 hover:border-white/20 hover:bg-white/5'
    }`;

  // 已连接素材缩略图（静态展示，不可点击跳转；播放角标 + 时长徽标）
  const refThumb = upstreamVideo ? (
    <div
      className="relative h-9 w-16 rounded-lg overflow-hidden border border-white/20"
      title="已连接素材"
      data-testid="smart-quick-ref-thumb"
    >
      <video
        src={upstreamVideo.videoUri}
        muted
        playsInline
        preload="metadata"
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* 播放角标 */}
      <span className="absolute inset-0 flex items-center justify-center">
        <Play size={12} fill="currentColor" className="text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.7)]" />
      </span>
      {/* 时长徽标 */}
      {durationLabel && (
        <span className="absolute right-0.5 bottom-0.5 rounded bg-black/65 px-1 py-px text-[9px] leading-none text-white/90 tabular-nums">
          {durationLabel}
        </span>
      )}
    </div>
  ) : null;

  // 效果描述输入（内嵌 3 行 / 展开态撑满剩余高度；Enter 换行，Ctrl/Cmd+Enter 发送）
  const renderTextarea = (className: string, rows?: number) => (
    <textarea
      value={draftText}
      onChange={(e) => onDraftChange(e.target.value)}
      onKeyDown={(e) => {
        // 阻断画布全局快捷键（如 Delete 删节点）；中文输入法组词回车不触发发送
        e.stopPropagation();
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !e.nativeEvent.isComposing) {
          e.preventDefault();
          handleSend();
        }
      }}
      placeholder="描述想剪成什么效果"
      rows={rows}
      className={className}
      data-testid="smart-quick-input"
    />
  );

  // 底行工具栏两态复用：模式 / 规格下拉 + 发送（内嵌与全屏展开同构）
  const footer = (
    <div className="flex items-center gap-1.5">
      <div className="relative">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setOpenMenu(openMenu === 'mode' ? null : 'mode');
          }}
          onMouseDown={(e) => e.stopPropagation()}
          className={triggerCls(openMenu === 'mode')}
          title="剪辑模式"
          data-testid="smart-quick-mode"
        >
          <span>{modeName}</span>
          <ChevronDown size={12} />
        </button>
        {openMenu === 'mode' && (
          <div
            className="absolute bottom-full mb-2 left-0 z-10 min-w-[172px] rounded-xl bg-[#262626] border border-white/10 shadow-xl overflow-hidden"
            onMouseDown={(e) => e.stopPropagation()}
          >
            {EDIT_MODES.map((m) => (
              <button
                key={m.id}
                type="button"
                className={menuItemCls(m.id === mode)}
                onClick={(e) => {
                  e.stopPropagation();
                  setMode(m.id);
                  setOpenMenu(null);
                }}
              >
                <span>{m.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="relative">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setOpenMenu(openMenu === 'spec' ? null : 'spec');
          }}
          onMouseDown={(e) => e.stopPropagation()}
          className={triggerCls(openMenu === 'spec')}
          title="画面比例与分辨率"
          data-testid="smart-quick-spec"
        >
          <span>{ratio} · {resolution}{durationLabel ? ` · ${durationLabel}` : ''}</span>
          <ChevronDown size={12} />
        </button>
        {openMenu === 'spec' && (
          <div
            className="absolute bottom-full mb-2 left-0 z-10 w-[220px] rounded-xl bg-[#262626] border border-white/10 shadow-xl overflow-hidden"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="px-3 pt-2.5 pb-1 text-[11px] text-white/40">画面比例</div>
            <div className="flex gap-1 px-2">
              {RATIOS.map((r) => (
                <button
                  key={r}
                  type="button"
                  className={chipCls(r === ratio)}
                  onClick={(e) => {
                    e.stopPropagation();
                    setRatio(r);
                  }}
                >
                  {r}
                </button>
              ))}
            </div>
            <div className="px-3 pt-2.5 pb-1 text-[11px] text-white/40">分辨率</div>
            <div className="flex gap-1 px-2 pb-2">
              {RESOLUTIONS.map((res) => (
                <button
                  key={res}
                  type="button"
                  className={chipCls(res === resolution)}
                  onClick={(e) => {
                    e.stopPropagation();
                    setResolution(res);
                  }}
                >
                  {res}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex-1" />

      {/* 右组（对齐 EditorPanel 底行右组）：翻译 / ⚡积分 / 发送 */}
      <div className="flex items-center gap-3">
        {/* 翻译：中英互译·自动方向，译文替换输入草稿；翻译中转 Loader2 防重入 */}
        <button
          type="button"
          disabled={translating || !draftText.trim() || !translateModelId}
          onClick={(e) => {
            e.stopPropagation();
            handleTranslate();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          title={translateModelId ? '翻译（中英互译）' : '暂无可用翻译模型'}
          className="text-white/40 hover:text-white/70 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
        >
          {translating ? <Loader2 size={16} className="animate-spin" /> : <Languages size={16} />}
        </button>
        {/* 积分图标改用顶部积分胶囊同款 token-icon.svg（原 Zap）；P1 实装：发送仅打开面板不扣费，按翻译对话模型口径预估 */}
        <span
          className="flex items-center gap-1 text-[12px] text-white/40"
          title="本次生成预计消耗积分（以打开面板后的实际生成为准）"
        >
          <img src="/icons/home/token-icon.svg" alt="" width={12} height={12} draggable={false} />
          {/* 预留三位数积分宽度（tabular-nums 等宽数字），位数变化不引起布局跳动 */}
          <span className="min-w-[22px] tabular-nums text-left">{getModelCost(translateModelId, agentModels)}</span>
        </span>
        {/* 对齐 EditorPanel 发送按钮基准（v1.4.12）：32×32 / 圆角 8px / 白底 + send-arrow.svg */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleSend();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          className="shrink-0 flex items-center justify-center w-8 h-8 bg-white rounded-[8px] text-[#222222] transition-transform hover:scale-105 cursor-pointer"
          title="发送并打开智能剪辑面板（Ctrl+Enter）"
          data-testid="smart-quick-send"
        >
          <img src="/icons/send-arrow.svg" alt="发送" width={16} height={16} style={{ objectFit: 'contain' }} draggable={false} />
        </button>
      </div>
    </div>
  );

  // 全屏展开态（镜像脚本节点 v1.4.17/20）：createPortal 到 body 的 fixed 居中面板——DOM 脱离 React Flow
  // transform 缩放/平移上下文，React 树仍挂在本组件内（展开尺寸等 state 不丢）；
  // 结构 = 标题行 + 已连接素材 + 大编辑区 + 同款底栏 + 右下角拖拽手柄；遮罩 / Minimize2 还原为内嵌底部面板
  if (expanded) {
    return createPortal(
      <>
        {/* 背景遮罩：四周压暗 + 毛玻璃模糊；点击遮罩还原为内嵌底部面板 */}
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-md"
          style={{ zIndex: 89 }}
          onClick={() => setExpanded(false)}
        />
        <div
          ref={rootRef}
          className="nodrag flex flex-col rounded-2xl bg-[#262626]/[0.97] backdrop-blur-xl border border-white/10 shadow-2xl p-5"
          style={{
            position: 'fixed',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: panelSize.w,
            height: panelSize.h,
            zIndex: 90,
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          data-testid="smart-quick-expanded"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 text-[15px] font-medium text-white/90 truncate" title={data.title}>
              {data.title || '智能剪辑'}
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(false);
              }}
              title="还原为底部面板"
              className="shrink-0 text-white/40 hover:text-white/70 transition-colors cursor-pointer"
            >
              <Minimize2 size={16} />
            </button>
          </div>

          {refThumb && <div className="mt-3 flex">{refThumb}</div>}

          {/* 展开态编辑区：撑满剩余高度 */}
          <div className="flex-1 min-h-0 mt-3">
            {renderTextarea('h-full w-full resize-none bg-transparent outline-none text-[13px] font-medium leading-relaxed text-slate-200 placeholder:text-white/20')}
          </div>

          <div className="mt-3">{footer}</div>

          {/* 右下角拖拽手柄：nwse-resize，最大视口 92% */}
          <div
            onMouseDown={handleResizeStart}
            title="拖拽调整面板尺寸"
            className="absolute bottom-0 right-0 w-5 h-5 cursor-nwse-resize flex items-end justify-end p-1 text-white/30 hover:text-white/60 transition-colors"
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <path d="M9 1 L1 9" />
              <path d="M9 5 L5 9" />
              <path d="M9 8 L8 9" />
            </svg>
          </div>
        </div>
      </>,
      document.body,
    );
  }

  return (
    <div
      ref={rootRef}
      className="rounded-2xl bg-[#262626]/95 backdrop-blur-2xl border border-white/10 shadow-2xl p-3"
      onMouseDown={(e) => e.stopPropagation()}
      data-testid="smart-edit-quick-input"
    >
      {/* 顶行：左上参考入口 + 右上全屏按钮（均不再打开剪辑面板，对齐脚本节点底部面板） */}
      <div className="flex items-center justify-between">
        {refThumb ?? (
          /* P2 接线点：参考素材上传（对齐脚本节点底部面板「+参考图」占位，禁用态不打开剪辑面板） */
          <button
            type="button"
            disabled
            className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-xl bg-white/[0.06] border border-white/[0.10] text-[12px] font-medium text-white/50 cursor-not-allowed"
            data-testid="smart-quick-ref"
          >
            <Plus size={14} />
            参考
          </button>
        )}
        {/* 全屏按钮：切换到 Portal 居中展开面板 */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(true);
          }}
          onMouseDown={(e) => e.stopPropagation()}
          className="h-7 w-7 rounded-lg flex items-center justify-center text-white/40 hover:text-white/70 transition-colors cursor-pointer"
          title="全屏"
          data-testid="smart-quick-open"
        >
          <Maximize2 size={15} />
        </button>
      </div>

      {/* 中部：多行效果描述 */}
      {renderTextarea('mt-2 w-full resize-none bg-transparent outline-none text-[13px] font-medium leading-relaxed text-slate-200 placeholder:text-white/20', 3)}

      {/* 底行：模式 / 规格下拉 + 发送 */}
      <div className="mt-1.5">{footer}</div>
    </div>
  );
});

SmartEditQuickInputComponent.displayName = 'SmartEditQuickInput';
export const SmartEditQuickInput = SmartEditQuickInputComponent;
